import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NotificationApi } from 'src/app/_shared/api/notification.api';
import { CookieStorageService } from 'src/app/shared/service/cookie-storage.service';
import { RealtimeRefreshService } from 'src/app/shared/service/realtime-refresh/realtime-refresh.service';
import {
  AppNotification,
  NotificationStatusUpdatedEvent,
  SocketService,
} from 'src/app/shared/service/socket/socket.service';
import { NotificationState } from '../state/notification.state';

interface PropertyNotificationSocketEvent {
  type: string;
  propertyIds: Array<string | number>;
  referenceTypes: string[];
  createdAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private realtimeInitialized = false;
  private notificationPageActive = false;
  private realtimeSubscriptions = new Subscription();
  private userNotificationsSubject = new BehaviorSubject<AppNotification[]>([]);

  /**
 * Prevent the same live Socket.IO event from
 * triggering multiple refreshes.
 */
  private handledLiveNotificationIds =
    new Set<string>();

  readonly userNotifications$ = this.userNotificationsSubject.asObservable();

  private currentNotificationParams: any = {
    limit: 10,
    offset: 0,
    sortBy: 'newNotificationDate',
    sortOrder: 'desc',
    sortType: null,
    sortSearch: null,
    sortStatus: false,
  };

  constructor(
    private snackBar: MatSnackBar,
    private notificationState: NotificationState,
    private notificationApi: NotificationApi,
    private socketService: SocketService,
    private realtimeRefreshService: RealtimeRefreshService,
    private cookieStorageService: CookieStorageService
  ) { }

  initializeRealtimeNotifications(): void {
    if (this.realtimeInitialized) {
      return;
    }

    this.realtimeInitialized = true;

    // Load both sources once. Property data remains in NotificationState for
    // the existing property-card page; the unified inbox has its own stream.
    this.getPropertyNotifications(this.currentNotificationParams);
    this.loadUserNotifications({ limit: 100, offset: 0, unreadOnly: false });

    this.realtimeSubscriptions.add(
      this.socketService.ready$.subscribe(() => {
        // Recover Socket.IO events that may have been missed while disconnected.
        this.loadUserNotifications({ limit: 100, offset: 0, unreadOnly: false });

        if (this.notificationPageActive) {
          this.getPropertyNotifications(this.currentNotificationParams);
        }
      })
    );

    this.realtimeSubscriptions.add(
      this.socketService.notifications$.subscribe(
        (notification) => {
          if (!notification?.id) {
            return;
          }

          /*
           * Always update the notification inbox.
           */
          this.upsertUserNotification(notification);

          /*
           * Avoid processing the same live Socket.IO
           * notification more than once.
           */
          if (
            this.handledLiveNotificationIds.has(
              notification.id
            )
          ) {
            return;
          }

          this.handledLiveNotificationIds.add(
            notification.id
          );

          /*
           * RealtimeRefreshService performs the route check.
           *
           * It refreshes only when:
           * 1. category is report
           * 2. status is created or failed
           * 3. current route matches actionUrl
           *
           * Otherwise this does nothing.
           */

          this.realtimeRefreshService
            .handleNotification(notification);

          const snackbarStyle =
            notification.status === 'failed'
              ? 'snackbar-error'
              : 'snackbar-success';

          this.openSnackBar(
            notification.message,
            snackbarStyle
          );
        }
      )
    );

    this.realtimeSubscriptions.add(
      this.socketService.notificationStatusUpdated$.subscribe((event) => {
        this.applyNotificationStatusEvent(event);
      })
    );

    // Preserve the existing detailed property-page refresh behavior.
    this.realtimeSubscriptions.add(
      this.socketService
        .on<PropertyNotificationSocketEvent>('property-notifications:updated')
        .subscribe((event) => {
          if (event.type === 'PROPERTY_MONITORING_TEST') {
            this.openSnackBar('Socket notification test received', 'snackbar-success');
          }

          if (this.notificationPageActive) {
            this.getPropertyNotifications(this.currentNotificationParams);
          }
        })
    );

    this.socketService.connect();
  }

  setNotificationPageActive(isActive: boolean): void {
    this.notificationPageActive = isActive;

    if (isActive) {
      this.getPropertyNotifications(this.currentNotificationParams);
      this.loadUserNotifications({ limit: 100, offset: 0, unreadOnly: false });
    }
  }

  destroyRealtimeNotifications(): void {
    this.realtimeSubscriptions.unsubscribe();
    this.realtimeSubscriptions =
      new Subscription();

    this.realtimeInitialized = false;
    this.notificationPageActive = false;

    this.handledLiveNotificationIds.clear();
    this.userNotificationsSubject.next([]);
  }

  loadUserNotifications(params: any = {}): void {

    const idToken =
      this.cookieStorageService.getCookieValue('idToken');

    if (!idToken) {
      return;
    }

    const request = {
      limit: 100,
      offset: 0,
      unreadOnly: false,
      ...params,
    };

    this.notificationApi.getUserNotifications(request).subscribe(
      (res: any) => {
        if (!res || !Array.isArray(res.data)) {
          return;
        }

        const currentNotifications = this.userNotificationsSubject.value;
        const mergedNotifications = this.mergeNotifications(
          currentNotifications,
          res.data
        );
        this.userNotificationsSubject.next(mergedNotifications);
      },
      (error: any) => {
        console.error('Failed to load user notifications:', error);
      }
    );
  }

  getPropertyNotifications(params: any = {}): void {

    const idToken =
      this.cookieStorageService.getCookieValue('idToken');

    if (!idToken) {
      return;
    }

    this.currentNotificationParams = {
      ...this.currentNotificationParams,
      ...params,
    };

    this.notificationApi.getNotificationProperty(this.currentNotificationParams).subscribe(
      (res: any) => {
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          this.notificationState.notificationsValue = this.formatAlerts(res.data);
          this.notificationState.notificationTotalCountValue = res.totalCount || 0;
        } else {
          this.notificationState.notificationsValue = [];
          this.notificationState.notificationTotalCountValue = 0;
        }
      },
      () => {
        this.notificationState.notificationsValue = [];
        this.notificationState.notificationTotalCountValue = 0;
      }
    );
  }

  formatAlerts(data: any[] = []): any[] {
    return data.map((alert) => {
      const formattedAlert = { ...alert };

      switch (alert.referenceType || alert.type) {
        case 'L_NEW':
          formattedAlert.icon = 'newListed.svg';
          break;
        case 'L_PC':
          formattedAlert.icon = 'priceChange.svg';
          break;
        case 'L_SC':
          formattedAlert.icon = 'statusChanges.svg';
          break;
        case 'M_C':
          formattedAlert.icon = 'mortgageClose.svg';
          break;
        case 'M_NEW':
          formattedAlert.icon = 'mortgageOpen.svg';
          break;
        case 'M_LTV':
          formattedAlert.icon = 'LTV.svg';
          break;
        case 'P_OD':
          formattedAlert.icon = 'ownershipDuration.svg';
          break;
        case 'P_OSC':
          formattedAlert.icon = 'occupancy.svg';
          break;
        case 'D_ON':
          formattedAlert.icon = 'distressStatus.svg';
          break;
        case 'recommendation':
          formattedAlert.icon = 'recommendation.svg';
          break;
        case 'PRICE_RANGE_REPORT':
        case 'FINAL_VALUE_REPORT':
        case 'APPRAISER_REPORT':
          // Reuse an existing asset unless dedicated report icons are added.
          formattedAlert.icon = formattedAlert.icon || 'recommendation.svg';
          break;
      }

      return formattedAlert;
    });
  }

  changeNotificationStatus(params: any): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });

      this.notificationApi
        .UpdateNotificationStatusChange(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => observer.next({ response: res }),
          (error: any) => observer.error(error)
        );
    });
  }

  changeUserNotificationStatus(notificationIds: string[]): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });

      this.notificationApi
        .updateUserNotificationStatus(notificationIds)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            this.markUserNotificationsRead(notificationIds);
            observer.next({ response: res });
          },
          (error: any) => observer.error(error)
        );
    });
  }

  openSnackBar(message: string, style?: string): void {
    if (!message) {
      return;
    }

    this.snackBar.open(message, 'Dismiss', {
      duration: 5000,
      panelClass: [style || ''],
      verticalPosition: 'top',
    });
  }

  private upsertUserNotification(notification: AppNotification): boolean {
    if (!notification || !notification.id) {
      return false;
    }

    const notifications = [...this.userNotificationsSubject.value];
    const existingIndex = notifications.findIndex((item) => item.id === notification.id);
    const wasInserted = existingIndex === -1;

    if (wasInserted) {
      notifications.unshift(notification);
    } else {
      notifications[existingIndex] = {
        ...notifications[existingIndex],
        ...notification,
      };
    }

    notifications.sort(
      (first, second) =>
        new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
    );
    this.userNotificationsSubject.next(notifications);
    return wasInserted;
  }

  private mergeNotifications(
    currentNotifications: AppNotification[],
    loadedNotifications: AppNotification[]
  ): AppNotification[] {
    const notificationMap = new Map<string, AppNotification>();

    currentNotifications.forEach((notification) => {
      if (notification && notification.id) {
        notificationMap.set(notification.id, notification);
      }
    });

    // API results are authoritative for persisted read status.
    loadedNotifications.forEach((notification) => {
      if (notification && notification.id) {
        notificationMap.set(notification.id, notification);
      }
    });

    return Array.from(notificationMap.values()).sort(
      (first, second) =>
        new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
    );
  }

  private applyNotificationStatusEvent(event: NotificationStatusUpdatedEvent): void {
    if (!event || !Array.isArray(event.notificationIds)) {
      return;
    }

    this.markUserNotificationsRead(event.notificationIds);

    if (event.category === 'property' && this.notificationPageActive) {
      this.getPropertyNotifications(this.currentNotificationParams);
    }
  }

  private markUserNotificationsRead(notificationIds: string[]): void {
    const notificationIdSet = new Set(notificationIds);
    const notifications = this.userNotificationsSubject.value.map((notification) =>
      notificationIdSet.has(notification.id)
        ? { ...notification, readStatus: true }
        : notification
    );

    this.userNotificationsSubject.next(notifications);
  }
}
